import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { CalendarDays, ClipboardCheck, ReceiptText, Bot, LogOut, MailPlus, ClipboardList, Lightbulb, Users, FileText, MessageSquare, KeyRound, HeartPulse } from 'lucide-react';
import { getAllEggs, getTodayHolyDay, pickRandomEgg } from '../easterEggs';

import { API } from '../apiClient';
import { firstAllowedPath, getUserPermissions, hasPermission, requiredPermissionForPath } from '../permissions';
import { APARTMENTS, apartmentLabel, getApartmentIds } from '../apartments';

function api(path, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? {Authorization: `Bearer ${token}`} : {}),
      ...(options.headers || {})
    }
  }).then(async r => {
    if (!r.ok) throw new Error((await r.json()).detail || 'API error');
    return r.json();
  });
}

function AppLayout() {
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));
  const [bookings, setBookings] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 768);
  const [selectedApartment, setSelectedApartment] = useState(() => localStorage.getItem('hermes:view-apartment') || 'all');
  const [easterEgg, setEasterEgg] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  const userPermissions = getUserPermissions(user);
  const allowedApartmentIds = useMemo(() => getApartmentIds(user), [user]);
  const apartmentChoices = useMemo(() => {
    const visible = isAdmin ? APARTMENTS : APARTMENTS.filter((item) => allowedApartmentIds.includes(item.id));
    return visible.length ? visible : APARTMENTS;
  }, [allowedApartmentIds, isAdmin]);
  const selectedApartmentId = selectedApartment === 'all' ? null : Number(selectedApartment);
  const selectedApartmentLabel = selectedApartmentId ? apartmentLabel(selectedApartmentId) : 'Wszystkie apartamenty';

  useEffect(() => {
    if (!selectedApartment || selectedApartment === 'all') return;
    const numeric = Number(selectedApartment);
    const valid = apartmentChoices.some((item) => item.id === numeric);
    if (!valid) {
      setSelectedApartment('all');
      localStorage.setItem('hermes:view-apartment', 'all');
    }
  }, [apartmentChoices, selectedApartment]);

  async function load() {
    if (!localStorage.getItem('token')) return;
    try {
      const shouldLoadBookings = isAdmin || hasPermission(user, 'reservations') || hasPermission(user, 'billing');
      const shouldLoadJobs = isAdmin || hasPermission(user, 'cleaning') || hasPermission(user, 'expenses');
      const shouldLoadExpenses = isAdmin || hasPermission(user, 'expenses');
      const shouldLoadSummary = isAdmin || hasPermission(user, 'agent');
      const shouldLoadDashboard = isAdmin || hasPermission(user, 'nuki');
      const dashboardPath = selectedApartmentId ? `/dashboard?property_id=${encodeURIComponent(selectedApartmentId)}` : '/dashboard';
      const [b, j, e, s, d] = await Promise.all([
        shouldLoadBookings ? api('/bookings') : Promise.resolve([]),
        shouldLoadJobs ? api('/cleaning-jobs') : Promise.resolve([]),
        shouldLoadExpenses ? api('/expenses') : Promise.resolve([]),
        shouldLoadSummary ? api('/agent/summary') : Promise.resolve(null),
        shouldLoadDashboard ? api(dashboardPath) : Promise.resolve(null)
      ]);
      setBookings(b); setJobs(j); setExpenses(e); setSummary(s); setDashboard(d);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  }

  useEffect(() => {
    if(user) load().catch(console.error);
  }, [user, selectedApartmentId]);

  function logout() {
    localStorage.clear();
    setUser(null);
    navigate('/login');
  }

  function hasAccess(path) {
    if (!user) return false;
    if (path === '/dashboard' || path === '/pomysly' || path === '/zmiany') {
      return isAdmin;
    }
    const permission = requiredPermissionForPath(path);
    return hasPermission(user, permission);
  }

  useEffect(() => {
    if (user && !hasAccess(location.pathname)) {
      navigate(firstAllowedPath(user), { replace: true });
    }
  }, [location.pathname, user]);

  if (!user) {
    navigate('/login');
    return null;
  }

  const filteredBookings = selectedApartmentId ? bookings.filter((item) => Number(item.property_id) === selectedApartmentId) : bookings;
  const filteredJobs = selectedApartmentId ? jobs.filter((item) => Number(item.property_id) === selectedApartmentId) : jobs;
  const filteredExpenses = selectedApartmentId ? expenses.filter((item) => Number(item.property_id) === selectedApartmentId) : expenses;

  const staffCurrentJobs = filteredJobs.filter(j => j.is_current_action && j.status !== 'done').length;
  const adminCleaningQueue = filteredJobs.filter(j => j.status === 'in_progress' || j.is_current_action).length;
  const expensesQueue = filteredExpenses.filter(e => {
    const review = e.review_status || 'ok';
    const reimbursed = Boolean(e.reimbursement_confirmed_at || e.reimbursed_at || e.reimburse === 'done');
    return review === 'needs_clarification' || review === 'ai_pending' || (review === 'ok' && !reimbursed);
  }).length;

  const closeOnMobile = () => {
    if (window.innerWidth < 768) setCollapsed(true);
  };

  function revealChristEasterEgg() {
    const seed = (user?.username || 'guest') + ':' + (location.pathname || '/');
    const all = getAllEggs();
    const egg = pickRandomEgg(seed);
    setEasterEgg(egg);
    window.clearTimeout(window.__hermesChristEggTimer);
    window.__hermesChristEggTimer = window.setTimeout(() => setEasterEgg(null), 5500);
  }

  useEffect(() => {
    if (!user) return;
    const seed = (user.username || 'guest') + ':' + location.pathname;
    const egg = pickRandomEgg(seed);
    setEasterEgg(egg);
    window.clearTimeout(window.__hermesRouteEggTimer);
    window.__hermesRouteEggTimer = window.setTimeout(() => setEasterEgg(null), 4200);
  }, [location.pathname, user]);

  useEffect(() => {
    function onChristEgg(ev) {
      const detail = ev.detail || {};
      const seed = (user?.username || 'guest') + ':action:' + (detail.seed || Date.now());
      const egg = pickRandomEgg(seed);
      setEasterEgg(egg);
      window.clearTimeout(window.__hermesChristEggTimer);
      window.__hermesChristEggTimer = window.setTimeout(() => setEasterEgg(null), 4500);
    }
    window.addEventListener('hermes:christ-egg', onChristEgg);
    return () => window.removeEventListener('hermes:christ-egg', onChristEgg);
  }, [user]);

  const todayHoly = getTodayHolyDay();

  const navGroups = isAdmin ? [
    {
      title: 'Operacje',
      items: [
        { path: '/dashboard', label: 'Start', helper: 'stan mieszkania', icon: Bot, exact: true },
        { path: '/sprzatanie', label: 'Sprzątanie', helper: 'kolejka i checklisty', icon: ClipboardCheck, badge: adminCleaningQueue },
        { path: '/rezerwacje', label: 'Rezerwacje', helper: 'goście i pobyty', icon: CalendarDays },
        { path: '/rachunki', label: 'Rachunki', helper: 'dla gości', icon: FileText, exact: true },
        { path: '/wiadomosci', label: 'Wiadomości', helper: 'Paweł kategoryzuje', icon: MessageSquare, exact: true },
        { path: '/zakupy', label: 'Zakupy', helper: 'decyzje i zwroty', icon: ReceiptText, badge: expensesQueue },
        { path: '/finanse', label: 'Finanse', helper: 'przychody i koszty', icon: FileText, exact: true }
      ]
    },
    {
      title: 'Automatyzacje',
      items: [
        { path: '/import', label: 'Integracje', helper: 'telefon / Pulse + fallback', icon: MailPlus, exact: true },
        { path: '/health', label: 'Health', helper: 'pełny stan portalu', icon: HeartPulse, exact: true },
        { path: '/nuki', label: 'Nuki', helper: 'status i historia dostępu', icon: KeyRound, exact: true },
        { path: '/agent', label: 'Agent', helper: 'podsumowanie AI', icon: Bot, exact: true },
        { path: '/uzytkownicy', label: 'Użytkownicy', helper: 'konta i hasła', icon: Users, exact: true }
      ]
    },
    {
      title: 'Rozwój',
      items: [
        { path: '/pomysly', label: 'Pomysły', helper: 'co warto dodać', icon: Lightbulb, exact: true },
        { path: '/zmiany', label: 'Zmiany', helper: 'co wdrożono', icon: ClipboardList, exact: true }
      ]
    }
  ] : [
    {
      title: 'Twoje panele',
      items: [
        { path: '/sprzatanie', label: 'Sprzątanie', helper: 'kolejka i checklisty', icon: ClipboardCheck, badge: hasPermission(user, 'cleaning') ? staffCurrentJobs : 0, permission: 'cleaning' },
        { path: '/rezerwacje', label: 'Rezerwacje', helper: 'goście i pobyty', icon: CalendarDays, permission: 'reservations' },
        { path: '/rachunki', label: 'Rachunki', helper: 'dla gości', icon: FileText, exact: true, permission: 'billing' },
        { path: '/wiadomosci', label: 'Wiadomości', helper: 'Paweł kategoryzuje', icon: MessageSquare, exact: true, permission: 'messages' },
        { path: '/zakupy', label: 'Zakupy', helper: 'decyzje i zwroty', icon: ReceiptText, permission: 'expenses' },
        { path: '/finanse', label: 'Finanse', helper: 'przychody i koszty', icon: FileText, exact: true, permission: 'finance' },
        { path: '/import', label: 'Integracje', helper: 'telefon / Pulse + fallback', icon: MailPlus, exact: true, permission: 'integrations' },
        { path: '/health', label: 'Health', helper: 'pełny stan portalu', icon: HeartPulse, exact: true, permission: 'health' },
        { path: '/nuki', label: 'Nuki', helper: 'status i historia dostępu', icon: KeyRound, exact: true, permission: 'nuki' },
        { path: '/agent', label: 'Agent', helper: 'podsumowanie AI', icon: Bot, exact: true, permission: 'agent' },
        { path: '/uzytkownicy', label: 'Użytkownicy', helper: 'konta i hasła', icon: Users, exact: true, permission: 'users' }
      ].filter((item) => userPermissions.includes(item.permission))
    }
  ];

  const isActive = (item) => item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const badge = Number(item.badge || 0);
    return (
      <button
        key={item.path}
        className={isActive(item) ? 'active' : ''}
        onClick={() => { navigate(item.path); closeOnMobile(); }}
      >
        <Icon/>
        <span className="nav-button-content">
          <span className="nav-label-row">
            <span className="nav-label">{item.label}</span>
            {badge > 0 && <span className="nav-badge">{badge}</span>}
          </span>
          <span className="nav-helper">{item.helper}</span>
        </span>
      </button>
    );
  };

  const handleApartmentChange = (value) => {
    setSelectedApartment(value);
    localStorage.setItem('hermes:view-apartment', value);
  };

  return (
    <div className={`app${collapsed ? ' sidebar-collapsed' : ''}`}>
      <button className="sidebar-toggle" onClick={()=>setCollapsed(c=>!c)} aria-label="Toggle menu">
        {collapsed ? '☰' : '✕'}
      </button>
      <aside className={collapsed ? 'hidden' : ''}>
        <button
          type="button"
          className="logo logo-easter"
          onClick={revealChristEasterEgg}
          title="Pax Christi"
          aria-label="Hermes Rental — symboliczny znak pokoju"
        >
          <Bot/> <strong>Hermes Rental</strong><span aria-hidden="true" className="christogram">✝</span>
        </button>

        <div className="apartment-view-switch">
          <label>
            Widok apartamentu
            <select value={selectedApartment} onChange={(e) => handleApartmentChange(e.target.value)}>
              <option value="all">Wszystkie apartamenty</option>
              {apartmentChoices.map((item) => (
                <option key={item.id} value={String(item.id)}>{item.label}</option>
              ))}
            </select>
          </label>
          <small>Aktualnie: {selectedApartmentLabel}</small>
        </div>

        <nav aria-label="Główne menu">
          {navGroups.map(group => (
            <div className="nav-section" key={group.title}>
              <div className="nav-section-title">{group.title}</div>
              {group.items.map(renderNavItem)}
            </div>
          ))}
        </nav>
        <div className="userBox">
          <div className="userAvatar" aria-hidden="true">{(user.name || user.username || '?').slice(0, 1).toUpperCase()}</div>
          <div className="userMeta">
            <strong>{user.name}</strong>
            <span>@{user.username} · {isAdmin ? 'Administrator' : 'Personel'}</span>
          </div>
        </div>
        <button className="logout" onClick={logout}>
          <LogOut/> Wyloguj
        </button>
      </aside>
      <main>
        <Outlet context={{ bookings: filteredBookings, jobs: filteredJobs, expenses: filteredExpenses, summary, dashboard, reload: load, user, selectedApartment, selectedApartmentId, apartmentChoices }} />
      </main>
      {easterEgg && (
        <div
          className={`christ-easter-toast kind-${easterEgg.kind || 'quote'}`}
          role="status"
          onClick={() => setEasterEgg(null)}
          title="Kliknij, żeby zamknąć"
        >
          {easterEgg.kind === 'icon' && (
            <span className="christ-icon" aria-label={easterEgg.label}>{easterEgg.text}</span>
          )}
          {easterEgg.kind !== 'icon' && (
            <>
              <span className="christ-text">{easterEgg.text}</span>
              {easterEgg.ref && <span className="christ-ref">{easterEgg.ref}</span>}
            </>
          )}
        </div>
      )}
      {todayHoly && (
        <div className="christ-holy-banner" role="status">
          <strong>✝ {todayHoly.name}</strong>
          <span> · {todayHoly.text}</span>
        </div>
      )}
    </div>
  );
}

export default AppLayout;